import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { System } from '@app/core/resources/system';
import { SystemInformation, SystemInformationService } from '@app/core/resources/system-information';
import { SystemsService } from '@app/core/services/systems.service';
import { IJsonApiErrorDocument } from '@app/exceptions/services/jsonapi-error.interface';
import { filter } from 'rxjs';
import { PublicDealsComponent } from '@app/landing-page/components/public-deals/public-deals.component';
import { metakeywords_model } from './meta-keywords.model';
import { TranslateService } from '@ngx-translate/core';
import { header_es_AR } from 'assets/i18n/header-es-ar';
import { landing_es_AR } from 'assets/i18n/landing-es-ar';
import { PaymentQueueComponent } from './components/payment-queue/payment-queue.component';
import { SeoService } from '../core/services/seo.service';
import { OgService } from '@app/core/services/og.service';
import { NetworkService } from '@app/core/services/network.service';
import { PlatformBrowserService } from '@app/core/services/platform-browser.service';
import { CountryProvider } from '../core/country-provider/country-provider';
import { UserCountryService } from '../core/services/user-country.service';

@Component({
    selector: 'app-landing-for-systems',
    templateUrl: './landing-for-systems.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./landing-for-systems.component.scss']
})
export class LandingForSystemsComponent implements OnInit {
    @ViewChild(PublicDealsComponent) public emptyPublicDeals!: PublicDealsComponent;
    @ViewChild(PaymentQueueComponent) public emptyPaymentQueue!: PaymentQueueComponent;
    public system: System = new System();
    public paymentId: string = '';
    public textEmptyComponent: string = '';
    public coordinatedTransfers: number = 0;
    public sentTransaction: number = 0;
    public alternativeSystem: Array<string> = [];
    public arrayAux: Array<string> = ['skrill', 'advcash', 'aphold', 'banco', 'banco_ves', 'bitcoin'];
    public defaultSystem: Array<string> = ['banco', 'palpal', 'skrill'];
    public tagsBlog: string = '';
    private descriptionMetaKeywords: any = metakeywords_model;
    public systemHasData: boolean = true;
    public marketData: boolean = true;
    private countryProvider: CountryProvider = new CountryProvider();
    public countryName: string = '';

    public constructor(
        private systemsService: SystemsService,
        private activatedRoute: ActivatedRoute,
        private changeDetectorRef: ChangeDetectorRef,
        public networksService: NetworkService,
        private seoService: SeoService,
        private ogService: OgService,
        private router: Router,
        private translateService: TranslateService,
        public browserService: PlatformBrowserService,
        private userCountryService: UserCountryService,
        public systemInformationService: SystemInformationService
    ) {
        let langs: any = {
            ...header_es_AR,
            ...landing_es_AR
        };
        this.translateService.setTranslation('landing-system', langs, true);
        this.translateService.use('landing-system');
        this.selectDefaultCountry();
    }

    public ngOnInit(): void {
        this.activatedRoute.params.subscribe((params: Params) => {
            this.alternativeSystem = [];
            this.systemsService
                .get(params.system, { include: ['rates', 'currency', 'system_information', 'networks'], ttl: 0 })
                .pipe(
                    filter((system: System) => {
                        return system.source !== 'new';
                    })
                )
                .subscribe(
                    (system: System): void => {
                        if (!system.loaded) {
                            return;
                        }

                        if (system.attributes.replacement_system_id) {
                            this.router.navigate(['/comprar-vender/' + system.attributes.replacement_system_id]);
                        }
                        this.system = system;
                        this.seoService.addCanonical('comprar-vender/' + system.id);
                        this.ogService.addOpenGraphMetadata({ url: 'comprar-vender' });
                        this.seoService.addMetaName(
                            'keyword',
                            this.descriptionMetaKeywords.default.metaKeywords +
                                (!this.descriptionMetaKeywords[this.system.id]
                                    ? ''
                                    : this.descriptionMetaKeywords[this.system.id].metaKeywords)
                        );
                        this.seoService.addTitle(
                            `${this.system.attributes.name} | cómo retirar dinero de ${this.system.attributes.name} ${
                                this.countryName ? 'en ' + this.countryName : ''
                            } `
                        );
                        if (
                            !this.system.relationships.system_information.builded ||
                            !this.system.relationships.system_information.data ||
                            !this.system.relationships.system_information.data[0]
                        ) {
                            this.systemHasData = false;
                            const systemDefault: SystemInformation = new SystemInformation();
                            systemDefault.attributes.description = 'Pronto podrás ver más información sobre este método de intercambio';
                            this.system.relationships.system_information.data[0] = systemDefault;
                            this.changeDetectorRef.markForCheck();

                            return;
                        }
                        if (!system.relationships.system_information.data[0].attributes.description) {
                            system.relationships.system_information.data[0].attributes.description =
                                'Pronto podrás ver más información sobre este método de intercambio';
                        }
                        if (
                            system.relationships.system_information.data[0].attributes.qty_coordinated_transaction === 0 &&
                            system.relationships.system_information.data[0].attributes.qty_sent_transaction === 0 &&
                            system.relationships.system_information.data[0].attributes.qty_user_per_system === 0 &&
                            system.relationships.system_information.data[0].attributes.volume === 0 &&
                            (system.relationships.system_information.data[0].attributes.trend_by_day === '' ||
                                system.relationships.system_information.data[0].attributes.trend_by_day === '0') &&
                            (system.relationships.system_information.data[0].attributes.trend_by_weekday === '' ||
                                system.relationships.system_information.data[0].attributes.trend_by_weekday === '0')
                        ) {
                            this.marketData = false;
                        }
                        this.coordinatedTransfers =
                            this.system.relationships.system_information.data[0].attributes.qty_coordinated_transaction;
                        this.sentTransaction = this.system.relationships.system_information.data[0].attributes.qty_sent_transaction;
                        this.alternativeSystem = this.convertStringToArray(
                            this.system.relationships.system_information.data[0]?.attributes.alternative_system_ids,
                            '|'
                        );
                        this.tagsBlog = this.system.relationships.system_information.data[0]?.attributes.tags;
                        this.changeDetectorRef.markForCheck();
                    },
                    (error: IJsonApiErrorDocument) => {
                        if (error?.errors[0].status === '404') {
                            this.router.navigate(['/error'], { skipLocationChange: true });

                            return;
                        }
                        // eslint-disable-next-line rxjs/throw-error
                        throw error;
                    }
                );
        });
        this.activatedRoute.queryParams.subscribe((params: Params) => {
            if (!params.payment_id) {
                return;
            }
            this.paymentId = params.payment_id;
        });
        this.changeDetectorRef.markForCheck();
    }

    public convertStringToArray(text: string, separator: string): Array<string> {
        if (!text) {
            return [];
        }

        return text.split(separator);
    }

    private selectDefaultCountry(): void {
        this.userCountryService.getCountry().subscribe((country): void => {
            console.log(country);
            this.countryName =
                country.code !== 'es-US' && country.code !== 'en-US' ? this.countryProvider.getCountry(country.code).countryName : '';
        });
    }

    public isEmpty(obj: any): boolean {
        return Object.keys(obj).length === 0;
    }
}
